import { useLoyaltyStatusQuery, useMeQuery, useProfileQuery } from '@generated'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import React, { FC } from 'react'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { useAuth } from '@lib/hooks/use-auth'
import { FloatingServiceMenu, Footer, Header, Page, PageTransition } from '@components/Page'
import { Spinner } from '@components/gassan-ui'
import { MembershipPageNavigation } from './Navigation'

export type MembershipPageType =
  | 'overview'
  | 'my-membership'
  | 'privileges'
  | 'services'
  | 'service-detail'
  | 'orders'
  | 'order-detail'
  | 'documents'
  | 'giftcards'
  | 'preferences'
  | 'wishlist'
type MembershipPageProps = {
  children: React.ReactNode
  size?: 'small' | 'regular'
  pageType: MembershipPageType
}

const motionPropsContent = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.2 },
}

export const MembershipPage: FC<MembershipPageProps> = ({
  children,
  size = 'regular',
  pageType,
  ...rest
}) => {
  const { t } = useTranslation('navigation')
  const [{ fetching: fetchingLoyalty }] = useLoyaltyStatusQuery()
  const [{ fetching: fetchingMe }] = useMeQuery()
  const [{ fetching: fetchingProfile }] = useProfileQuery()
  const { locales } = useRouter()
  const { isLoggedIn, isLoading } = useAuth()

  // Preload data that is used throughout the membership pages
  // to prevent flickering elements that rely on this data
  const hasLoadedEssentialData =
    !fetchingLoyalty && !fetchingMe && !fetchingProfile && !isLoading && isLoggedIn

  return (
    <Page
      title={t(`membership.${pageType}.pageTitle`)}
      languageAlternates={buildLanguageAlternateNavigation(locales, `membership.${pageType}.url`)}
      {...rest}
    >
      <FloatingServiceMenu />
      <Header initialVariant="absolute" />
      <MembershipPageNavigation />
      <PageTransition>
        <AnimatePresence>
          {hasLoadedEssentialData ? (
            <motion.div {...motionPropsContent}>{children}</motion.div>
          ) : (
            <div className="flex h-[50vh] items-center justify-center">
              <Spinner />
            </div>
          )}
        </AnimatePresence>
        <Footer />
      </PageTransition>
    </Page>
  )
}
